/**
 * The king-admin edit state of the /docs page (pure helpers; DocsPage owns
 * the object). One entry is edited at a time; the draft mirrors the form's
 * fields so a re-render mid-edit (the search box rewrites `<main>` on every
 * keystroke) restores what was typed.
 */

import type { WikiAuditRow, WikiEntry, WikiTextPatch } from '../../shared/types/layerWiki.js';
import { docsCopy } from './docsCopy.js';
import { WikiApiError } from './WikiApiClient.js';

export interface EditDraft {
  sl: string;
  cs: string;
  note: string;
}

export interface EditHistory {
  key: string;
  rows: WikiAuditRow[];
  /** The history request failed — the section says so instead of "no edits yet". */
  failed: boolean;
}

export interface EditState {
  isKingAdmin: boolean;
  /** The entry whose description is being edited, or null. */
  editingKey: string | null;
  draft: EditDraft;
  /** A save or restore is in flight. */
  busy: boolean;
  /** Slovenian copy of the last failure (form or history section), or null. */
  error: string | null;
  history: EditHistory | null;
}

export function initialEditState(): EditState {
  return {
    isKingAdmin: false,
    editingKey: null,
    draft: { sl: '', cs: '', note: '' },
    busy: false,
    error: null,
    history: null,
  };
}

/** Open the form for `entry`, prefilled with its current text. */
export function beginEdit(state: EditState, entry: WikiEntry): void {
  state.editingKey = entry.key;
  state.draft = { sl: entry.description, cs: entry.descriptionCs ?? '', note: '' };
  state.error = null;
}

export function cancelEdit(state: EditState): void {
  state.editingKey = null;
  state.error = null;
}

/** The PATCH body: a blank Czech field clears the Czech text; the note travels only when written. */
export function patchFromDraft(draft: EditDraft): WikiTextPatch {
  const patch: WikiTextPatch = {
    descriptionSl: draft.sl,
    descriptionCs: draft.cs.trim() === '' ? null : draft.cs,
  };
  const note = draft.note.trim();
  if (note !== '') patch.note = note;
  return patch;
}

/** Slovenian copy for a failed save / restore — by machine code, then by status. */
export function errorCopyFor(err: unknown): string {
  const e = docsCopy.edit.errors;
  if (err instanceof WikiApiError) {
    switch (err.code) {
      case 'empty_sl':
        return e.emptySl;
      case 'no_change':
        return e.noChange;
      case 'not_found':
      case 'audit_not_found':
        return e.notFound;
      default:
        break;
    }
    if (err.status === 401 || err.status === 403) return e.forbidden;
    if (err.status === 404) return e.notFound;
  }
  return e.generic;
}
